import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtZMW, fmtDate, COMPANY } from "@/lib/helpers";

export default function QuotationPrint() {
  const { id } = useParams();
  const [q, setQ] = useState(null);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/quotations/${id}`); setQ(data); } catch (e) { if (process.env.NODE_ENV !== "production") console.debug("non-fatal:", e); }
    })();
  }, [id]);

  useEffect(() => {
    if (q) setTimeout(() => window.print(), 500);
  }, [q]);

  if (!q) return <div className="p-10">Loading…</div>;

  return (
    <div className="print-area max-w-4xl mx-auto p-10 bg-white text-black font-sans">
      <div className="no-print mb-6 flex items-center justify-between">
        <div className="phizo-label">Print Preview</div>
        <button onClick={() => window.print()} className="phizo-btn-primary">Print / Save PDF</button>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between pb-6 border-b-4 border-[#09090b]">
        <div className="flex items-center gap-4">
          <img src={COMPANY.logo} alt="Phizo" className="h-16 w-16 object-contain" />
          <div>
            <div className="font-heading text-3xl font-black tracking-tighter">{COMPANY.name}</div>
            <div className="text-sm text-[#52525b]">{COMPANY.address}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="phizo-label">Quotation</div>
          <div className="font-heading text-4xl font-black tracking-tighter mono">{q.number}</div>
          <div className="text-xs text-[#52525b] mt-1">Issued {fmtDate(q.created_at)}</div>
          {q.valid_until && (
            <div className="text-xs text-[#52525b]">Valid until {fmtDate(q.valid_until)}</div>
          )}
        </div>
      </div>

      {/* Meta */}
      <div className="grid grid-cols-2 gap-6 py-6">
        <div>
          <div className="phizo-label mb-2">Bill To</div>
          <div className="font-semibold text-lg">{q.customer}</div>
          {q.customer_address && <div className="text-sm text-[#52525b] whitespace-pre-line mt-1">{q.customer_address}</div>}
          {q.customer_email && <div className="text-sm text-[#52525b]">{q.customer_email}</div>}
          {q.customer_phone && <div className="text-sm text-[#52525b]">{q.customer_phone}</div>}
        </div>
        <div className="text-right">
          <div className="phizo-label mb-2">Prepared By</div>
          <div className="font-semibold">{q.created_by_name}</div>
          <div className="text-sm text-[#52525b]">{COMPANY.name}</div>
        </div>
      </div>

      {/* Items */}
      <table className="w-full border-t-2 border-b-2 border-[#09090b]">
        <thead>
          <tr className="border-b border-[#e4e4e7]">
            <th className="text-left py-3 phizo-label">#</th>
            <th className="text-left py-3 phizo-label">Description</th>
            <th className="text-right py-3 phizo-label">Qty</th>
            <th className="text-right py-3 phizo-label">Unit Price</th>
            <th className="text-right py-3 phizo-label">Total</th>
          </tr>
        </thead>
        <tbody>
          {q.items.map((it, idx) => (
            <tr key={idx} className="border-b border-[#e4e4e7]">
              <td className="py-3 mono text-sm">{idx + 1}</td>
              <td className="py-3">
                <div className="font-medium">{it.description}</div>
                {it.sku && <div className="text-xs text-[#a1a1aa] mono">{it.sku}</div>}
              </td>
              <td className="py-3 text-right mono">{it.quantity}</td>
              <td className="py-3 text-right mono">{fmtZMW(it.unit_price)}</td>
              <td className="py-3 text-right mono font-semibold">{fmtZMW(it.quantity * it.unit_price)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals */}
      <div className="flex justify-end py-6">
        <div className="w-72 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="phizo-label">Subtotal</span>
            <span className="mono">{fmtZMW(q.subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="phizo-label">Tax ({q.tax_rate}%)</span>
            <span className="mono">{fmtZMW(q.tax)}</span>
          </div>
          <div className="flex justify-between pt-3 border-t-2 border-[#09090b]">
            <span className="phizo-label">Grand Total (ZMW)</span>
            <span className="font-heading font-black text-2xl">{fmtZMW(q.total)}</span>
          </div>
        </div>
      </div>

      {q.notes && (
        <div className="pt-6 border-t border-[#e4e4e7]">
          <div className="phizo-label mb-2">Notes</div>
          <div className="text-sm text-[#52525b] whitespace-pre-line">{q.notes}</div>
        </div>
      )}

      <div className="pt-10 mt-10 border-t border-[#e4e4e7] text-xs text-[#a1a1aa] text-center">
        Thank you for your business. — {COMPANY.name}
      </div>
    </div>
  );
}
